/**
 * activityRunMachine — l'exécution d'une activité, de l'ouverture à la sortie : une machine pure.
 *
 * Ce que l'ancien `ActivityScreen` tenait dans une douzaine de `useState`, de refs et de rappels
 * d'`useApiStatus` (`_submitClicked`, `finalizeRetriedRef`, `status`, `notFoundError`…) :
 *  - `working` : l'élève fait l'activité, sa copie part pièce par pièce (la file, `copyOutbox`) ;
 *  - `finishing` : « J'ai fini » — la copie finit de partir, puis la finalisation part. Comme
 *    aujourd'hui, « J'ai fini » se presse pendant qu'une photo part : il l'attend. Une pièce en échec
 *    est relancée au même geste ; si elle échoue encore, l'élève le voit (`copy_failed`) ;
 *  - la finalisation et ses issues (`finalizeOutcome`) : un succès ; « déjà soumise », un succès sans
 *    écran de fin ; « pas encore sauvegardé », UN renvoi avec les clés de la copie, puis l'échec
 *    visible avec « Réessayer » ; 404, l'activité n'existe plus ; hors ligne, 5xx ou refus : l'échec
 *    visible avec « Réessayer » (le 5xx était muet) ;
 *  - après le succès : la correction quand Sam corrige la copie (`correction_wait`), puis les
 *    félicitations, d'où l'écran enchaîne lui-même ;
 *  - rendue en Dialogue (`stays`, le masteur est là, Wajih 25/10) : pas d'attente de la correction,
 *    les félicitations, puis l'élève revient sur l'activité rendue (`submitted`) au lieu de passer à la
 *    suivante. La correction arrive plus tard, sans le couper : le live l'annonce, l'activité rendue la
 *    montre (Wajih, 26/10) ;
 *  - la sortie demandée (le retour) : une question, avec ou sans copie, comme l'ancien `onExit` ;
 *  - en live, le tuteur peut la rendre à la place de l'élève (« Soumettre ») : elle passe « validée »
 *    et se relit, comme l'ancien écran passait à `ActivityFinishedScreen` ; le tuteur peut aussi
 *    l'archiver (`ACTIVITY_GONE`, comme un relevé en 404).
 *
 * Le réducteur ne lit ni l'heure ni le réseau : les effets (`useActivityRun`) lui donnent des
 * événements. Chaque transition laisse un fil d'Ariane (le crochet appelle la télémétrie).
 */
import { type ApiFinalizeResult } from "./celebration/activityCongrats"
import { type FinalizeFailureKind } from "./finalizeOutcome"

export type RunPhase =
    /** L'élève fait l'activité. */
    | "working"
    /** « J'ai fini » : la copie finit de partir, puis la finalisation. */
    | "finishing"
    /** Rendue, Sam corrige la copie : l'attente, puis la correction. */
    | "correction"
    /** Les félicitations. */
    | "congrats"
    /** Fini sans écran de fin (« déjà soumise ») : l'écran enchaîne (l'activité suivante, ou la liste). */
    | "done"
    /** L'activité n'existe plus (404) : l'écran le dit, puis enchaîne. */
    | "deleted"
    /** L'élève est sorti sans rendre. */
    | "left"
    /** Le tuteur l'a rendue à la place de l'élève (en live) : elle se relit. */
    | "validated"
    /** Rendue en Dialogue, l'élève revenu dessus après ses félicitations : elle se relit, sa correction quand elle arrive. */
    | "submitted"

/** Ce qui a empêché « J'ai fini » d'aboutir, que l'élève voit avec « Réessayer ». */
export type RunFailure = "copy_failed" | "not_saved" | "offline" | "server" | "rejected"

export type RunState = {
    activityId: number | null
    phase: RunPhase
    /** Une finalisation est en vol. */
    finalizing: boolean
    /** Le renvoi unique après « pas encore sauvegardé » est fait. */
    notSavedResent: boolean
    failure: RunFailure | null
    /** Les fois où la copie n'est pas partie sur cette activité : la boîte change de mots dès la deuxième. */
    copyFailures: number
    /** La réponse de la finalisation ; `null` après « déjà soumise ». */
    result: ApiFinalizeResult | null
    /** La question de la sortie : avec une copie (elle sera rendue), ou sans. */
    exitQuestion: "with_work" | "without_work" | null
    /**
     * Soumise en Dialogue : l'élève reste sur l'activité. Lu au geste, pas à la réponse : le mode peut
     * changer pendant que la copie part.
     */
    stays: boolean
}

export type RunEvent =
    | { type: "OPENED"; activityId: number | null }
    /** « J'ai fini » ; `stays` : en Dialogue, l'élève restera sur l'activité. */
    | { type: "FINISH_REQUESTED"; stays?: boolean }
    | { type: "COPY_FAILED" }
    | { type: "FINALIZE_STARTED" }
    | { type: "FINALIZE_SUCCEEDED"; result: ApiFinalizeResult }
    | { type: "FINALIZE_FAILED"; kind: FinalizeFailureKind; canResend: boolean }
    | { type: "FAILURE_DISMISSED" }
    | { type: "CORRECTION_DONE" }
    | { type: "BACK_REQUESTED"; hasWork: boolean }
    | { type: "BACK_CANCELLED" }
    | { type: "EXIT_CONFIRMED" }
    /** Un relevé (chrono, journal, suppression) a répondu 404, ou le tuteur l'a archivée : elle n'existe plus. */
    | { type: "ACTIVITY_GONE" }
    /** Le tuteur l'a rendue à la place de l'élève (`tutorFinalizedActivity`). */
    | { type: "TUTOR_FINALIZED" }
    /** « Revenir à l'activité », depuis les félicitations d'une copie soumise en Dialogue. */
    | { type: "BACK_TO_ACTIVITY" }

export function initialRunState(activityId: number | null = null): RunState {
    return {
        activityId,
        phase: "working",
        finalizing: false,
        notSavedResent: false,
        failure: null,
        copyFailures: 0,
        result: null,
        exitQuestion: null,
        stays: false,
    }
}

/** En Dialogue, la correction ne s'attend pas : l'élève a ses félicitations, la correction arrive après. */
function afterSuccess(result: ApiFinalizeResult, stays: boolean): RunPhase {
    return result.correction_wait != null && !stays ? "correction" : "congrats"
}

export function activityRunReducer(state: RunState, event: RunEvent): RunState {
    switch (event.type) {
        case "OPENED":
            // Une autre activité (le tuteur en pousse une, « Activité suivante avec Sam ») : tout repart.
            return event.activityId === state.activityId ? state : initialRunState(event.activityId)
        case "FINISH_REQUESTED":
            if (state.phase !== "working" || state.finalizing) return state
            return { ...state, phase: "finishing", failure: null, exitQuestion: null, stays: event.stays === true }
        case "COPY_FAILED":
            if (state.phase !== "finishing" || state.finalizing) return state
            return { ...state, phase: "working", failure: "copy_failed", copyFailures: state.copyFailures + 1 }
        case "FINALIZE_STARTED":
            if (state.phase !== "finishing" || state.finalizing) return state
            return { ...state, finalizing: true }
        case "FINALIZE_SUCCEEDED":
            if (!state.finalizing) return state
            return { ...state, finalizing: false, result: event.result, phase: afterSuccess(event.result, state.stays) }
        case "FINALIZE_FAILED": {
            if (!state.finalizing) return state
            const settled = { ...state, finalizing: false }
            switch (event.kind) {
                case "already_submitted":
                    // Sans écran de fin : en Dialogue, l'élève reste dessus, elle se relit.
                    return { ...settled, phase: state.stays ? "submitted" : "done", result: null }
                case "not_found":
                    return { ...settled, phase: "deleted" }
                case "not_saved":
                    // Le serveur n'a pas le journal : une fois, la finalisation repart avec les clés S3 de
                    // la copie (il recolle depuis elles) ; sans clé, ou au second refus, l'élève le voit.
                    if (event.canResend && !state.notSavedResent) {
                        return { ...settled, notSavedResent: true }
                    }
                    return { ...settled, phase: "working", failure: "not_saved" }
                default:
                    return { ...settled, phase: "working", failure: event.kind }
            }
        }
        case "FAILURE_DISMISSED":
            return state.failure == null ? state : { ...state, failure: null }
        case "CORRECTION_DONE":
            return state.phase === "correction" ? { ...state, phase: "congrats" } : state
        case "BACK_REQUESTED":
            if (state.phase !== "working") return state
            return { ...state, exitQuestion: event.hasWork ? "with_work" : "without_work" }
        case "BACK_CANCELLED":
            return state.exitQuestion == null ? state : { ...state, exitQuestion: null }
        case "EXIT_CONFIRMED":
            if (state.exitQuestion === "with_work") {
                // « Soumettre et quitter » (hors live) : la copie est rendue, et l'élève s'en va.
                return { ...state, exitQuestion: null, phase: "finishing", failure: null, stays: false }
            }
            if (state.exitQuestion === "without_work") return { ...state, exitQuestion: null, phase: "left" }
            return state
        case "ACTIVITY_GONE":
            // Rendue, une activité peut disparaître de la liste sans que l'élève y perde rien.
            return state.phase === "working" || state.phase === "finishing"
                ? { ...state, phase: "deleted", finalizing: false, exitQuestion: null }
                : state
        case "TUTOR_FINALIZED":
            // Comme l'ancien : seulement tant que l'élève ne l'a pas rendue lui-même.
            return state.phase === "working" || (state.phase === "finishing" && !state.finalizing)
                ? { ...state, phase: "validated", exitQuestion: null, failure: null }
                : state
        case "BACK_TO_ACTIVITY":
            return state.phase === "congrats" && state.stays ? { ...state, phase: "submitted" } : state
        default:
            return state
    }
}

/** La finalisation doit partir : « J'ai fini », rien en vol, et la copie arrivée (`copySettled`). */
export function shouldFinalize(state: RunState, copySettled: boolean): boolean {
    return state.phase === "finishing" && !state.finalizing && state.failure == null && copySettled
}
